import React, { useState, useEffect, useRef } from "react";
import "./tabs.css";
import {
    FiDownload,
    FiEye,
    FiTrash2,
    FiRefreshCw,
    FiUploadCloud,
    FiFile,
} from "react-icons/fi";
import { useParams } from "react-router-dom";
import { StlViewer } from "react-stl-viewer";
import {
    FaFilePdf,
    FaFileArchive,
    FaFileImage,
    FaCubes, // STL icon placeholder
} from "react-icons/fa";

import stlIcon from "./assets/stl.jpeg"; // Import your STL icon
import zipIcon from "./assets/zip-icon.png"; // Import your ZIP icon
import przIcon from "./assets/prz-icon.jpg"; // Import your PRZ icon
const Tabs = () => {
    const [activeTab, setActiveTab] = useState("details");
    const [notes, setNotes] = useState([]);
    const [newNote, setNewNote] = useState("");
    const [selectedImage, setSelectedImage] = useState(null);
    const [showImageModal, setShowImageModal] = useState(false);
    const [imageAction, setImageAction] = useState("");
    const [currentEditingImage, setCurrentEditingImage] = useState(null);
    const [imageFile, setImageFile] = useState(null);
    const { id } = useParams();
    const getExt = (s = "") =>
        s.toLowerCase().split(".").pop()?.split("?")[0] || "";
    const isStlFile = (path = "") => getExt(path) === "stl";
    const [imageSections, setImageSections] = useState({
        section1: [],
        section1_5: [],
        section2: [],
        section3: [],
    });
    const modalRef = useRef(null);
    const getFileIcon = (fileName) => {
        if (!fileName) return <FiFile size={40} />;

        const ext = fileName.split(".").pop().toLowerCase();

        switch (ext) {
            case "jpg":
            case "jpeg":
            case "png":
                return <FaFileImage size={40} color="#4cafef" />;
            case "stl": // Yahan custom image
                return (
                    <img
                        src={stlIcon}
                        alt="STL File"
                        style={{ width: 100, height:  100 }}
                    />
                );
            case "slddrw":
                return <FaCubes size={80} color="#9c27b0" />; // CAD placeholder
            case "zip":
                return <FaFileArchive size={80} color="#FCD116" />;
            case "rar":
                return (
                    <img
                        src={zipIcon}
                        alt="Rar File"
                        style={{ width: 100, height:  100 }}
                    />
                );
            case "pdf":
                return <FaFilePdf size={80} color="#e53935" />;
            case "prz":
                return (
                    <img
                        src={przIcon}
                        alt="Prz File"
                        style={{ width: 100, height:  100 }}
                    />
                );
            default:
                return <FiFile size={80} color="#FCD116" />;
        }
    };
    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await fetch(
                    `https://backend.hira-tech.co.uk/inventory/api/manufacturing/${id}/`
                );
                if (!response.ok) {
                    throw new Error("Failed to fetch manufacturing data");
                }
                const data = await response.json();

                if (data.notes) {
                    setNotes(Array.isArray(data.notes) ? data.notes : [data.notes]);
                } else {
                    setNotes([]);
                }

                const getFileExtension = (url) => {
                    const parts = url.split('.');
                    return parts.length > 1
                        ? parts[parts.length - 1].toLowerCase().split('?')[0]
                        : "";
                };

                const section1 = data.images
                    ? data.images
                        .filter((img) => img.image_type === 'image')
                        .map((img) => ({
                            id: img.id,
                            url: img.image,
                            title: "Image File",
                            date: img.uploaded_at
                                ? new Date(img.uploaded_at).toLocaleDateString()
                                : "N/A",
                            updated_date: img.uploaded_at
                                ? new Date(img.uploaded_at).toLocaleDateString()
                                : "N/A",
                            file: img.image.split('/').pop(),
                            from: img.uploaded_by || "Admin",
                        }))
                    : [];

                const section1_5 = data.images
                    ? data.images
                        .filter((img) => img.image_type === 'cad')
                        .map((img) => ({
                            id: img.id,
                            url: img.image,
                            title: "CAD File",
                            date: img.uploaded_at
                                ? new Date(img.uploaded_at).toLocaleDateString()
                                : "N/A",
                            updated_date: img.uploaded_at
                                ? new Date(img.uploaded_at).toLocaleDateString()
                                : "N/A",
                            file: img.image.split('/').pop(),
                            from: img.uploaded_by || "Admin",
                        }))
                    : [];

                const section2 = data.images
                    ? data.images
                        .filter((img) => img.image_type === 'zip')
                        .map((img) => ({
                            id: img.id,
                            url: img.image,
                            title: "Zip File",
                            date: img.uploaded_at
                                ? new Date(img.uploaded_at).toLocaleDateString()
                                : "N/A",
                            updated_date: img.uploaded_at
                                ? new Date(img.uploaded_at).toLocaleDateString()
                                : "N/A",
                            file: img.image.split('/').pop(),
                            from: img.uploaded_by || "Admin",
                        }))
                    : [];

                const section3 = data.images
                    ? data.images
                        .filter((img) => img.image_type === 'other')
                        .map((img) => ({
                            id: img.id,
                            url: img.image,
                            title: `Other File (${getFileExtension(img.image).toUpperCase()})`,
                            date: img.uploaded_at
                                ? new Date(img.uploaded_at).toLocaleDateString()
                                : "N/A",
                            updated_date: img.uploaded_at
                                ? new Date(img.uploaded_at).toLocaleDateString()
                                : "N/A",
                            file: img.image.split('/').pop(),
                            from: img.uploaded_by || "Admin",
                        }))
                    : [];

                setImageSections({
                    section1,
                    section1_5,
                    section2,
                    section3,
                });
            } catch (error) {
                console.error("Failed to fetch manufacturing data:", error);
            }
        };

        if (id) {
            fetchData();
        }
    }, [id]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (modalRef.current && !modalRef.current.contains(event.target)) {
                setShowImageModal(false);
            }
        };

        if (showImageModal) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [showImageModal]);

    const handleAddNote = async () => {
        if (newNote.trim()) {
            const note = { content: newNote };
            try {
                const response = await fetch(
                    `https://backend.hira-tech.co.uk/inventory/api/manufacturing/${id}/notes/`,
                    {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify(note),
                    }
                );

                if (!response.ok) {
                    const responseData = await response.json();
                    throw new Error(
                        `Failed to add note: ${
                            responseData.detail || responseData.message || response.statusText
                        }`
                    );
                }

                const responseData = await response.json();
                setNotes([...notes, responseData]);
                setNewNote("");
                alert("Note added successfully!");
            } catch (error) {
                console.error("Failed to add note:", error.message);
                alert(`Failed to add note: ${error.message}`);
            }
        }
    };

    const getFileType = (fileName) => {
        const ext = fileName.split('.').pop().toLowerCase();
        if (['jpg', 'jpeg', 'png'].includes(ext)) return 'image';
        if (ext === 'stl') return 'stl';
        if (['zip', 'rar'].includes(ext)) return 'zip';
        return 'other';
    };

    const categorizeFiles = (files) => {
        const sections = {
            section1: [],
            section1_5: [],
            section2: [],
            section3: [],
        };

        files.forEach(file => {
            const type = getFileType(file.file || file.image.name);
            const section =
                type === 'image' ? 'section1' :
                    type === 'stl' ? 'section1_5' :
                        type === 'zip' ? 'section2' : 'section3';

            sections[section].push(file);
        });

        return sections;
    };

    const handleImageAction = (action, image, sectionKey, index) => {
        setImageAction(action);
        setCurrentEditingImage({ ...image, sectionKey, index });
        if (action === "view" || action === "replace") {
            setSelectedImage(image.url);
        }
        setShowImageModal(true);
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setImageFile(file);
        const ext = getExt(file.name);

        if (["jpg", "jpeg", "png"].includes(ext)) {
            const reader = new FileReader();
            reader.onload = (event) => setSelectedImage(event.target.result);
            reader.readAsDataURL(file);
        } else {
            // For non-image files, including STL, don't set a preview
            setSelectedImage(null);
        }
    };

    const determineSectionKey = (fileExtension) => {
        const imageExtensions = ["jpg", "jpeg", "png"];
        const cadExtensions = ["stl", "slddrw"];
        const zipExtensions = ["zip", "rar"];

        if (imageExtensions.includes(fileExtension)) return "section1";
        if (cadExtensions.includes(fileExtension)) return "section1_5";
        if (zipExtensions.includes(fileExtension)) return "section2";
        return "section3";
    };

    const handleAddImage = async () => {
        if (!imageFile) {
            alert("Please select a file to upload.");
            return;
        }

        const fileExtension = imageFile.name.split('.').pop().toLowerCase();
        const sectionKey = determineSectionKey(fileExtension);

        const formData = new FormData();
        formData.append("image", imageFile);

        try {
            const response = await fetch(
                `https://backend.hira-tech.co.uk/inventory/api/manufacturing/${id}/images/`,
                {
                    method: "POST",
                    body: formData,
                }
            );

            if (!response.ok) {
                const responseData = await response.json();
                throw new Error(`Failed to add file: ${JSON.stringify(responseData)}`);
            }

            const newImage = await response.json();
            const updatedSections = { ...imageSections };

            let title;
            if (sectionKey === "section1") {
                title = "Image File";
            } else if (sectionKey === "section1_5") {
                title = "CAD File";
            } else if (sectionKey === "section2") {
                title = "Zip File";
            } else {
                title = `Other File (${fileExtension.toUpperCase()})`;
            }

            updatedSections[sectionKey].push({
                id: newImage.id,
                url: newImage.image,
                title: title,
                date: newImage.uploaded_at
                    ? new Date(newImage.uploaded_at).toLocaleDateString()
                    : "N/A",
                updated_date: newImage.uploaded_at
                    ? new Date(newImage.uploaded_at).toLocaleDateString()
                    : "N/A",
                file: imageFile.name,
                from: newImage.uploaded_by || "Admin",
            });

            setImageSections(updatedSections);
            setShowImageModal(false);
            setImageFile(null);
            setSelectedImage(null);
            alert("File added successfully!");
        } catch (error) {
            console.error("Failed to add file:", error);
            alert(`Failed to add file: ${error.message}`);
        }
    };

    const handleReplaceImage = async (sectionKey, imageId, index) => {
        if (!imageFile) {
            alert("Please select a file to upload.");
            return;
        }

        const fileExtension = imageFile.name.split(".").pop().toLowerCase();
        const newSectionKey = determineSectionKey(fileExtension);

        if (sectionKey !== newSectionKey) {
            alert("Please upload a file of the same type as the original.");
            return;
        }

        const imageType =
            sectionKey === "section1"
                ? "image"
                : sectionKey === "section1_5"
                    ? "cad"
                    : sectionKey === "section2"
                        ? "zip"
                        : "other";
        const formData = new FormData();
        formData.append("image", imageFile);
        formData.append("image_type", imageType);

        try {
            const deleteResponse = await fetch(
                `https://backend.hira-tech.co.uk/inventory/api/manufacturing/${id}/images/${imageId}/`,
                {
                    method: "DELETE",
                    headers: { "Content-Type": "application/json" },
                }
            );

            if (!deleteResponse.ok) {
                let deleteErrorData = {};
                try {
                    deleteErrorData = await deleteResponse.json();
                } catch {
                    deleteErrorData = {
                        status: deleteResponse.status,
                        statusText: deleteResponse.statusText,
                    };
                }
                console.error("Delete request failed:", {
                    url: deleteResponse.url,
                    status: deleteResponse.status,
                    data: deleteErrorData,
                });
                throw new Error(
                    `Failed to delete old file: ${JSON.stringify(deleteErrorData)}`
                );
            }

            const response = await fetch(
                `https://backend.hira-tech.co.uk/inventory/api/manufacturing/${id}/images/`,
                {
                    method: "POST",
                    body: formData,
                }
            );

            if (!response.ok) {
                const responseData = await response.json();
                throw new Error(
                    `Failed to replace file: ${JSON.stringify(responseData)}`
                );
            }

            const newImage = await response.json();
            const updatedSections = { ...imageSections };
            updatedSections[sectionKey][index] = {
                id: newImage.id,
                url: newImage.image,
                title:
                    sectionKey === "section1"
                        ? "Image File"
                        : sectionKey === "section1_5"
                            ? "CAD File"
                            : sectionKey === "section2"
                                ? "Zip File"
                                : `Other File (${fileExtension.toUpperCase()})`,
                date: newImage.uploaded_at
                    ? new Date(newImage.uploaded_at).toLocaleDateString()
                    : "N/A",
                updated_date: newImage.uploaded_at
                    ? new Date(newImage.uploaded_at).toLocaleDateString()
                    : "N/A",
                file: newImage.image.split("/").pop(),
                from: newImage.uploaded_by || "Admin",
            };

            setImageSections(updatedSections);
            setShowImageModal(false);
            setImageFile(null);
            setSelectedImage(null);
            alert("File replaced successfully!");
        } catch (error) {
            console.error("Failed to replace file:", error);
            alert(`Failed to replace file: ${error.message}`);
        }
    };

    const handleDeleteImage = async (sectionKey, imageId, index) => {
        if (!window.confirm("Are you sure you want to delete this file?")) {
            return;
        }

        try {
            const response = await fetch(
                `https://backend.hira-tech.co.uk/inventory/api/manufacturing/${id}/images/${imageId}/`,
                {
                    method: "DELETE",
                    headers: {
                        "Content-Type": "application/json",
                        "X-CSRFToken": getCookie("csrftoken"),
                    },
                }
            );

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(`Failed to delete file: ${JSON.stringify(errorData)}`);
            }

            const updatedSections = { ...imageSections };
            updatedSections[sectionKey].splice(index, 1);
            setImageSections(updatedSections);
            alert("File deleted successfully!");
        } catch (error) {
            console.error("Failed to delete file:", error);
            alert(`Failed to delete file: ${error.message}`);
        }
    };

    function getCookie(name) {
        let cookieValue = null;
        if (document.cookie && document.cookie !== "") {
            const cookies = document.cookie.split(";");
            for (let i = 0; i < cookies.length; i++) {
                const cookie = cookies[i].trim();
                if (cookie.substring(0, name.length + 1) === name + "=") {
                    cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
                    break;
                }
            }
        }
        return cookieValue;
    }

    const downloadImage = (imageUrl, originalName) => {
        fetch(imageUrl)
            .then((response) => response.blob())
            .then((blob) => {
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                let filename = originalName || imageUrl.split('/').pop().split('?')[0];
                link.download = filename;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
            })
            .catch((err) => console.error('Error downloading file:', err));
    };

    const isImageFile = (url) => {
        const imageExtensions = ["jpg", "jpeg", "png"];
        const ext = url.split(".").pop().toLowerCase().split("?")[0];
        return imageExtensions.includes(ext);
    };

    return (
        <div className="lot-container">
            <div className="lot-tabs">
                <button
                    className={`lot-tab ${activeTab === "details" ? "active" : ""}`}
                    onClick={() => setActiveTab("details")}
                >
                    Details
                </button>
                <button
                    className={`lot-tab ${activeTab === "notes" ? "active" : ""}`}
                    onClick={() => setActiveTab("notes")}
                >
                    Notes
                </button>
            </div>

            <div className="lot-tab-content">
                {activeTab === "details" ? (
                    <div className="details-content">
                        <div className="header-row">
                            <h2>File Details</h2>
                            <button
                                className="add-file-button"
                                onClick={() => {
                                    setImageAction("add");
                                    setCurrentEditingImage(null);
                                    setSelectedImage(null);
                                    setImageFile(null);
                                    setShowImageModal(true);
                                }}
                            >
                                <FiUploadCloud className="upload-icon" />
                                Add File
                            </button>
                        </div>

                        <div className="image-section">
                            <h3>Image Files</h3>
                            <div className="image-cards">
                                {imageSections.section1.map((image, index) => (
                                    <div key={image.id} className="image-card">
                                        <div
                                            className="image-container"
                                            onClick={() => downloadImage(image.url, image.file)}
                                        >
                                            {getFileIcon(image.file)}
                                            <img
                                                src={image.url}
                                                alt={image.title}
                                                className="image-preview"
                                            />
                                        </div>
                                        <div className="image-actions">
                                            <button
                                                onClick={() =>
                                                    handleImageAction("view", image, "section1", index)
                                                }
                                                title="View File"
                                            >
                                                <FiEye />
                                            </button>
                                            <button
                                                onClick={() => downloadImage(image.url, image.file)}
                                                title="Download File"
                                            >
                                                <FiDownload />
                                            </button>
                                            <button
                                                onClick={() =>
                                                    handleImageAction("replace", image, "section1", index)
                                                }
                                                title="Replace File"
                                            >
                                                <FiRefreshCw />
                                            </button>
                                            <button
                                                onClick={() =>
                                                    handleDeleteImage("section1", image.id, index)
                                                }
                                                title="Delete File"
                                                className="delete"
                                            >
                                                <FiTrash2 />
                                            </button>
                                        </div>
                                        <div className="image-data">
                                            <p><strong>Date:</strong> {image.date}</p>
                                            <p><strong>Updated:</strong> {image.updated_date}</p>
                                            <p><strong>File:</strong> {image.file}</p>
                                            <p><strong>From:</strong> {image.from}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="image-section">
                            <h3>Intraoral Scan Files</h3>
                            <div className="image-cards">
                                {imageSections.section1_5.map((image, index) => (
                                    <div key={image.id} className="image-card">
                                        <div
                                            className="image-container"
                                            onClick={() => downloadImage(image.url, image.file)}
                                        >
                                            {getFileIcon(image.file)}
                                        </div>
                                        <div className="image-actions">
                                            <button
                                                onClick={() => downloadImage(image.url, image.file)}
                                                title="Download File"
                                            >
                                                <FiDownload />
                                            </button>
                                            <button
                                                onClick={() =>
                                                    handleImageAction("view", image, "section1_5", index)
                                                }
                                                title="View File"
                                            >
                                                <FiEye />
                                            </button>
                                            <button
                                                onClick={() =>
                                                    handleDeleteImage("section1_5", image.id, index)
                                                }
                                                title="Delete File"
                                                className="delete"
                                            >
                                                <FiTrash2 />
                                            </button>
                                        </div>
                                        <div className="image-data">
                                            <p><strong>Date:</strong> {image.date}</p>
                                            <p><strong>Updated:</strong> {image.updated_date}</p>
                                            <p><strong>File:</strong> {image.file}</p>
                                            <p><strong>From:</strong> {image.from}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="image-section">
                            <h3>Zip Files</h3>
                            <div className="image-cards">
                                {imageSections.section2.map((image, index) => (
                                    <div key={image.id} className="image-card">
                                        <div
                                            className="image-container"
                                            onClick={() => downloadImage(image.url, image.file)}
                                        >
                                            {getFileIcon(image.file)}
                                        </div>
                                        <div className="image-actions">
                                            <button
                                                onClick={() => downloadImage(image.url, image.file)}
                                                title="Download File"
                                            >
                                                <FiDownload />
                                            </button>
                                            <button
                                                onClick={() =>
                                                    handleImageAction("replace", image, "section2", index)
                                                }
                                                title="Replace File"
                                            >
                                                <FiRefreshCw />
                                            </button>
                                            <button
                                                onClick={() =>
                                                    handleDeleteImage("section2", image.id, index)
                                                }
                                                title="Delete File"
                                                className="delete"
                                            >
                                                <FiTrash2 />
                                            </button>
                                        </div>
                                        <div className="image-data">
                                            <p><strong>Date:</strong> {image.date}</p>
                                            <p><strong>Updated:</strong> {image.updated_date}</p>
                                            <p><strong>File:</strong> {image.file}</p>
                                            <p><strong>From:</strong> {image.from}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="image-section">
                            <h3>Other Files</h3>
                            <div className="image-cards">
                                {imageSections.section3.map((image, index) => (
                                    <div key={image.id} className="image-card">
                                        <div
                                            className="image-container"
                                            onClick={() => downloadImage(image.url, image.file)}
                                        >
                                            {getFileIcon(image.file)}
                                        </div>
                                        <div className="image-actions">
                                            <button
                                                onClick={() => downloadImage(image.url, image.file)}
                                                title="Download File"
                                            >
                                                <FiDownload />
                                            </button>
                                            <button
                                                onClick={() =>
                                                    handleImageAction("replace", image, "section3", index)
                                                }
                                                title="Replace File"
                                            >
                                                <FiRefreshCw />
                                            </button>
                                            <button
                                                onClick={() =>
                                                    handleDeleteImage("section3", image.id, index)
                                                }
                                                title="Delete File"
                                                className="delete"
                                            >
                                                <FiTrash2 />
                                            </button>
                                        </div>
                                        <div className="image-data">
                                            <p><strong>Date:</strong> {image.date}</p>
                                            <p><strong>Updated:</strong> {image.updated_date}</p>
                                            <p><strong>File:</strong> {image.file}</p>
                                            <p><strong>From:</strong> {image.from}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="notes-content">
                        <h2>Production Notes</h2>
                        <div className="notes-list">
                            {notes.length > 0 ? (
                                notes.map((note, index) => (
                                    <div key={note.id || index} className="note">
                                        <div className="note-header">
                                            <span className="note-author">Admin</span>
                                            <span className="note-date">
                        {note.created_at
                            ? new Date(note.created_at).toLocaleDateString()
                            : "No date"}
                      </span>
                                        </div>
                                        <div className="note-text">{note.content}</div>
                                    </div>
                                ))
                            ) : (
                                <p>No notes available.</p>
                            )}
                        </div>
                        <div className="add-note">
              <textarea
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  placeholder="Add a new note..."
                  rows="4"
              />
                            <button onClick={handleAddNote} disabled={!newNote.trim()}>
                                Add Note
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {showImageModal && (
                <div className="image-modal">
                    <div className="modal-content" ref={modalRef}>
                        <button
                            className="close-modal"
                            onClick={() => setShowImageModal(false)}
                        >
                            ×
                        </button>
                        {imageAction === "add" || imageAction === "replace" ? (
                            <div className="replace-form aesthetic-upload">
                                <h3>{imageAction === "add" ? "Add File" : "Replace File"}</h3>
                                <div
                                    className="upload-area"
                                    onClick={() =>
                                        document.getElementById("image-upload").click()
                                    }
                                >
                                    <FiUploadCloud className="cloud-icon" size={60} />
                                    <p>Click or drop a file here</p>
                                </div>
                                <input
                                    type="file"
                                    id="image-upload"
                                    accept="*/*"
                                    onChange={handleFileChange}
                                    className="file-input-hidden"
                                />
                                {imageFile && (
                                    <div className="file-info">
                                        <strong>Selected:</strong> {imageFile.name}
                                    </div>
                                )}
                                {imageFile && isImageFile(imageFile.name) && (
                                    <img
                                        src={selectedImage}
                                        alt="Preview"
                                        className="modal-image-preview"
                                        style={{ maxWidth: "100%", maxHeight: "300px", marginTop: "12px" }}
                                    />
                                )}
                                {imageFile && !isImageFile(imageFile.name) && (
                                    <p style={{ marginTop: "12px" }}>
                                        Preview not available for this file type.
                                    </p>
                                )}
                                <button
                                    className="replace-button"
                                    onClick={() =>
                                        imageAction === "add"
                                            ? handleAddImage()
                                            : handleReplaceImage(
                                                currentEditingImage.sectionKey,
                                                currentEditingImage.id,
                                                currentEditingImage.index
                                            )
                                    }
                                >
                                    {imageAction === "add" ? "Add File" : "Replace File"}
                                </button>
                            </div>
                        ) : (
                            <div className="view-image">
                                <div className="modal-image-container">
                                    {isImageFile(selectedImage) ? (
                                        <img
                                            src={selectedImage}
                                            alt={currentEditingImage?.title}
                                            className="modal-image-full"
                                        />
                                    ) : isStlFile(selectedImage || currentEditingImage?.file || currentEditingImage?.url) ? (
                                        <div style={{ width: "100%", height: "60vh" }}>
                                            <StlViewer
                                                url={selectedImage || currentEditingImage?.url}
                                                orbitControls
                                                shadows
                                                style={{ width: "100%", height: "100%" }}
                                                onError={(err) => console.error("STL load error:", err)}
                                            />
                                        </div>
                                    ) : (
                                        <div style={{ padding: 16 }}>
                                            <p>Preview not available. Use the download button above.</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default Tabs;